import React from 'react';

import { useNavigate } from 'react-router-dom';
import { reverse } from 'named-urls';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { GridEventListener, GridRowParams } from '@mui/x-data-grid';
import { InputAdornment } from '@mui/material';
import debounce from 'lodash/debounce';

import routes from 'core/routes';

import Grid from 'components/Grid';
import { FilterType } from 'components/Grid/types';

import { StyledTextField } from 'components/Input/styles';
import SearchIcon from 'components/Icons/Search';

import { VatixError } from 'components/Error/types';

import { AcknowledgementState, MyAcknowledgementRow } from 'utils/api/modules/entities/entities.types';

import { FiltersContainer, ResetButton, Spacer } from 'containers/Tasks/styles';

import StatusPicker from '../StatusPicker';

import { acknowledgementColumns } from './columns';
import { ACKNOWLEDGEMENT_STATUS_OPTIONS } from './constants';

const EMPTY_COPY = 'No acknowledgements yet — assigned acknowledgements will appear here.';
const FILTERED_EMPTY_COPY = 'No acknowledgements match the current filters';

const STATE_FIELD = 'state';

interface AcknowledgementsTabProps {
  onError: (error: VatixError) => void;
  onRefreshCallback: (callback: Function) => void;
}

const AcknowledgementsTab: React.FunctionComponent<AcknowledgementsTabProps> = ({
  onError,
  onRefreshCallback,
}): React.ReactElement => {
  const navigate = useNavigate();
  const gridApiRef = useGridApiRef();
  const refreshRef = React.useRef<Function | null>(null);

  // Empty selection = "All" → both Pending and Acknowledged shown. A non-empty selection
  // narrows the queue via the grid's `state` filter.
  const [statusFilter, setStatusFilter] = React.useState<AcknowledgementState[]>([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');

  const debouncedSetQuery = React.useMemo(() => debounce(setDebouncedQuery, 300), []);

  React.useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  const temporaryFilters = React.useMemo<FilterType[]>(
    () => (debouncedQuery ? [{ field: 'query', operator: 'is', value: debouncedQuery }] : []),
    [debouncedQuery]
  );

  const hasActiveFilters = statusFilter.length > 0 || searchQuery !== '';

  const handleRefreshCallback = (callback: Function): void => {
    refreshRef.current = callback;
    onRefreshCallback(callback);
  };

  const triggerRefresh = (): void => {
    refreshRef.current?.();
  };

  // Mirror useTasksFilters.updateFilterModel: replace the `state` items in the grid filter model
  // with one `in` item per selected status, then refetch. Multiple values are joined by the grid
  // with `;;` and split server-side (MyAcknowledgementsFilter.filter_state).
  const handleStatusChange = (value: AcknowledgementState[]): void => {
    setStatusFilter(value);
    if (!gridApiRef.current) return;

    const otherItems = gridApiRef.current.state.filter.filterModel.items.filter((item) => item.field !== STATE_FIELD);
    const stateItems = value.map((state) => ({
      id: `${STATE_FIELD}_${state}`,
      field: STATE_FIELD,
      operator: 'in',
      value: state,
    }));

    gridApiRef.current.setFilterModel({
      ...gridApiRef.current.state.filter.filterModel,
      items: [...otherItems, ...stateItems],
    });
    triggerRefresh();
  };

  const handleResetFilters = (): void => {
    setSearchQuery('');
    setDebouncedQuery('');
    handleStatusChange([]);
  };

  const onRowClick: GridEventListener<'rowClick'> = (params: GridRowParams<MyAcknowledgementRow>): void => {
    navigate(reverse(routes.dashboard.acknowledgements.details, { acknowledgementId: params.row.uuid }));
  };

  return (
    <>
      <FiltersContainer>
        <StatusPicker value={statusFilter} onChange={handleStatusChange} options={ACKNOWLEDGEMENT_STATUS_OPTIONS} />
        {hasActiveFilters && <ResetButton onClick={handleResetFilters}>Reset filters</ResetButton>}
        <Spacer />
        <StyledTextField
          placeholder="Search"
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            debouncedSetQuery(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </FiltersContainer>
      <Grid
        basicColumns={acknowledgementColumns}
        onError={onError}
        onRefreshCallback={handleRefreshCallback}
        onRowClick={onRowClick}
        dataURL="/api/entities/documents/acknowledgements/"
        sortBy={{ field: 'dueDate', sort: 'asc' }}
        customNoRowsText={hasActiveFilters ? FILTERED_EMPTY_COPY : EMPTY_COPY}
        disableColumnMenu
        disableColumnSelector
        disableColumnFilter
        disableSavePreferences
        gridApiRef={gridApiRef}
        getRowId={(row) => row.uuid}
        showHeader={false}
        temporaryFilters={temporaryFilters}
      />
    </>
  );
};

export default AcknowledgementsTab;
